<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Config } from '@/Config'
import * as Server from '@/services/Server'
import * as Auth from '@/services/Auth'
import * as Alerts from '@/services/Alerts'
import PageHeader from '@/components/PageHeader.vue'
import ChipEventType from '@/components/ChipEventType.vue'
import ChipPoints from '@/components/ChipPoints.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import DateRange from '@/components/DateRange.vue'
import EventActivationButton from '@/components/EventActivationButton.vue'
import EventResetButton from '@/components/EventResetButton.vue'
import GamePlayButton from '@/components/GamePlayButton.vue'
import UserName from '@/components/UserName.vue'
import TrackPointsTable from '@/components/TrackPointsTable.vue'
import TrackTimesTable from '@/components/TrackTimesTable.vue'
import { formatUtcTime, getMonthName } from '@/lib/dates'
import { podiumClass } from '@/lib/podium'
import { formatDelta } from '@/lib/rating'

const route = useRoute()
const { t, te } = useI18n()

const eventId = computed(() => route.params.id)
const event = ref(null)
const loading = ref(true)
const error = ref(false)

const isAuthenticated = Auth.isAuthenticated()

const STATE_COLORS = {
  scheduled: 'blue-grey',
  running: 'green',
  finished: 'grey',
}

const stateColor = (state) => STATE_COLORS[state] ?? 'grey'

/**
 * Title of the page: an event that has a name of its own ("Championship of September") shows that
 * instead of its type, which is only the short label of the chips. The name is built from the month
 * the event opens in, so it does not depend on the current date.
 */
const eventTitle = computed(() => {
  const type = event.value?.eventType
  if (!type) return `#${eventId.value}`

  return te(`eventNames.${type}`)
    ? t(`eventNames.${type}`, { month: getMonthName(event.value.dateStart) })
    : t(`eventTypes.${type}`)
})

const breadcrumbs = computed(() => [
  { title: t('events.breadcrumb'), to: { name: 'events' } },
  { title: event.value ? t(`eventTypes.${event.value.eventType}`) : `#${eventId.value}` },
])

/** The player's result on the given track, when they already have one. */
const myResultFor = (trackId) => event.value?.myResults?.find(r => r.eventTrackId === trackId) ?? null

/**
 * The hardcore stage attempt on the given track, when the player has one that is not finished:
 * `running` while they are driving it, `abandoned` once it cannot go on (see EventStageLib on the
 * server). A finished attempt has a result instead, so the two never both exist.
 */
const myAttemptFor = (trackId) => event.value?.myAttempts?.find(a => a.eventTrackId === trackId) ?? null

/**
 * Whether the track holds a stage the player may erase and race again: a finished or abandoned one,
 * with a retry left in the event. A running attempt has to end first — erasing it would be a free
 * reset, which is the whole point of a hardcore event.
 */
const canRetryStage = (track) => Boolean(event.value?.playable
  && event.value?.hardcore
  && (event.value?.retriesLeft ?? 0) > 0
  && (myResultFor(track.id) || myAttemptFor(track.id)?.status === 'abandoned'))

const retryTrack = ref(null)
const retryConfirming = ref(false)
const retrying = ref(false)

const retryText = computed(() => {
  const trackName = retryTrack.value ? t(`tracks.${retryTrack.value.trackKey}`) : ''
  return t('events.retryText', { track: trackName, n: event.value?.retriesLeft ?? 0 })
})

const askRetryStage = (track) => {
  retryTrack.value = track
  retryConfirming.value = true
}

/** Spends one retry on that stage: the server erases it and hands back the updated event. */
const retryStage = async () => {
  if (!retryTrack.value) return

  retrying.value = true

  try {
    const response = await Server.del(Config.server.routes.api.events_retry_stage(event.value.id, retryTrack.value.trackKey))
    applyReset(response.data?.data ?? null)
    Alerts.success(t('events.retryDone'))
  } catch (error) {
    Alerts.error(error.response?.data?.message ?? t('events.retryError'))
  } finally {
    retrying.value = false
    retryTrack.value = null
  }
}

/** Who won the event: the first row of the standings, when the event has one. */
const winner = computed(() =>
  event.value?.standings?.find(row => row.position === 1)?.playerName ?? null
)

/**
 * The standings are read one track at a time — where every point was earned, or the gap to the best
 * time of each track — or as the plain list. Both families have the two views (TrackPointsTable and
 * TrackTimesTable) and open on the columns.
 */
const VIEW = { TRACKS: 'tracks', LIST: 'list' }
const standingsView = ref(VIEW.TRACKS)

/** A time event has no points: its track columns show the gap to the best time (see TrackTimesTable). */
const isTimeBased = computed(() => Boolean(event.value) && !event.value.pointsBased && !event.value.season)

/** Track keys of the columns: the tracks of the event, in their order. */
const trackKeys = computed(() => (event.value?.tracks ?? []).map(track => track.trackKey))

/** The standings in the shape the shared table reads (see TrackPointsTable). */
const standingsRows = computed(() => (event.value?.standings ?? []).map(row => ({
  id: row.userId,
  name: row.playerName,
  position: row.position,
  total: row.points,
  wins: row.wins,
  isMe: row.isMe,
  tracks: row.tracks ?? {},
})))

/**
 * The standings in the shape the time table reads (see TrackTimesTable): the sum of the times (the
 * gap to the leader rides its tooltip) and one cell per track with its own gap.
 */
const timeRows = computed(() => (event.value?.standings ?? []).map(row => ({
  id: row.userId,
  name: row.playerName,
  position: row.position,
  total: row.resultTime,
  gap: row.resultGapTime,
  isMe: row.isMe,
  tracks: row.tracks ?? {},
})))

/** The columns per track, on the screen and with something to show. */
const showTrackColumns = computed(() => (event.value?.pointsBased || isTimeBased.value)
  && standingsView.value === VIEW.TRACKS
  && (event.value?.standings?.length ?? 0) > 0)

const fetchEvent = () => {
  loading.value = true
  error.value = false

  Server.get(Config.server.routes.api.events_detail(eventId.value))
    .then(response => {
      event.value = response.data?.data ?? null
    })
    .catch(() => {
      error.value = true
      event.value = null
    })
    .finally(() => {
      loading.value = false
    })
}

/**
 * Takes what the reset button hands back: the event as it is now, with the entry gone and the
 * standings recomputed, so there is nothing to ask for again.
 */
const applyReset = (updated) => {
  if (updated) event.value = updated
}

/**
 * Takes the choice the activation button hands back (see components/EventActivationButton.vue):
 * only the flag changes, so the event object stays and keeps everything else the page shows.
 */
const applyActivation = (active) => {
  if (event.value) event.value.myActivation = active
}

onMounted(fetchEvent)
watch(eventId, fetchEvent)
</script>

<template>
  <main>
    <v-container>
      <v-breadcrumbs :items="breadcrumbs" class="px-0 pb-2">
        <template v-slot:divider>
          <v-icon>mdi-chevron-right</v-icon>
        </template>
      </v-breadcrumbs>

      <div v-if="loading" class="d-flex justify-center py-12">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <v-alert v-else-if="error" type="error" variant="tonal">
        {{ $t('events.loadError') }}
      </v-alert>

      <template v-else-if="event">
        <PageHeader
          :title="eventTitle"
          :subtitle="$t(`events.rules.${event.eventType}`)"
          icon="mdi-calendar-star"
        />

        <v-row>
          <!-- Tracks: first in the markup, but visually moved to the right (below on small screens) -->
          <v-col cols="12" md="8" class="order-2">
            <v-card elevation="3" rounded="lg" class="h-100 overflow-hidden">
              <div class="d-flex align-center ga-3 pa-4">
                <v-avatar color="primary" variant="tonal" size="48">
                  <v-icon size="28">mdi-flag-checkered</v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                  <h2 class="text-h6 font-weight-bold">{{ $t('events.tracks') }}</h2>
                  <p class="text-caption text-medium-emphasis mb-0">{{ $t('events.tracksSubtitle') }}</p>
                  <!-- A hardcore event spends a stage as soon as it starts: the retries are the
                       only way to race one of them again (see canRetryStage above). -->
                  <p
                    v-if="event.hardcore && event.playable && isAuthenticated"
                    class="text-caption text-medium-emphasis mb-0"
                  >
                    {{ $t('events.retriesLeft', { n: event.retriesLeft }) }}
                  </p>
                </div>
              </div>

              <v-divider />

              <v-table hover>
                <thead>
                  <tr>
                    <th style="width: 56px">#</th>
                    <th>{{ $t('events.track') }}</th>
                    <th v-if="event.usesLaps" class="text-center">{{ $t('events.laps') }}</th>
                    <th class="text-center">{{ $t(event.season ? 'events.yourTrackRating' : 'events.yourTrackResult') }}</th>
                    <th style="width: 48px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="track in event.tracks" :key="track.id">
                    <td class="text-medium-emphasis">{{ track.sortOrder }}</td>
                    <td>
                      <!-- The track name leads to the standings of that track -->
                      <RouterLink
                        :to="{ name: 'event_track', params: { id: event.id, trackKey: track.trackKey } }"
                        class="d-flex align-center ga-2 text-decoration-none text-high-emphasis"
                      >
                        <v-icon v-if="track.country" size="14">mdi-map-marker</v-icon>
                        <span class="font-weight-medium">{{ $t(`tracks.${track.trackKey}`) }}</span>
                        <span v-if="track.country" class="text-caption text-medium-emphasis">
                          {{ $t(`countries.${track.country}`) }}
                        </span>
                      </RouterLink>
                    </td>
                    <td v-if="event.usesLaps" class="text-center">{{ track.laps }}</td>
                    <td class="text-center">
                      <div class="d-inline-flex align-center ga-1">
                        <template v-if="myResultFor(track.id)">
                          <v-chip size="small" variant="tonal" label>
                            {{ $t('events.position') }} {{ myResultFor(track.id).position }}
                          </v-chip>
                          <v-chip size="small" color="primary" variant="flat" label>
                            <!-- A season has no time: the value on a track is its rating -->
                            {{ myResultFor(track.id).rating !== null
                              ? formatDelta(myResultFor(track.id).rating)
                              : myResultFor(track.id).resultTime }}
                          </v-chip>
                          <ChipPoints
                            v-if="myResultFor(track.id).points !== null"
                            :points="myResultFor(track.id).points"
                          />
                        </template>
                        <!-- A hardcore stage that is not finished: running or lost. It occupies
                             the track until a retry or "start over" gives it back. -->
                        <v-chip
                          v-else-if="myAttemptFor(track.id)"
                          size="small"
                          variant="tonal"
                          :color="myAttemptFor(track.id).status === 'running' ? 'info' : 'error'"
                          label
                        >
                          {{ $t(`events.stageState.${myAttemptFor(track.id).status}`) }}
                        </v-chip>
                        <span v-else class="text-medium-emphasis text-caption">{{ $t('events.todo') }}</span>
                        <v-btn
                          v-if="canRetryStage(track)"
                          icon="mdi-restart"
                          size="x-small"
                          variant="text"
                          color="warning"
                          :loading="retrying && retryTrack?.id === track.id"
                          :title="$t('events.retryStage')"
                          @click="askRetryStage(track)"
                        />
                      </div>
                    </td>
                    <td class="text-right">
                      <v-icon size="18">mdi-chevron-right</v-icon>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-card>
          </v-col>

          <!-- Call to action: left on wide screens, on top on small ones -->
          <v-col cols="12" md="4" class="order-1">
            <v-card elevation="3" rounded="lg" class="h-100 d-flex flex-column pa-4">
              <v-btn
                v-if="!isAuthenticated"
                :to="{ name: 'login' }"
                color="primary"
                size="large"
                class="w-100"
              >
                {{ $t('events.loginToParticipate') }}
              </v-btn>
              <GamePlayButton
                v-else-if="event.playable"
                :label="$t('play.playNow')"
                size="large"
                class="w-100"
              />
              <!-- An event that is over has nothing left to do: a line of text says who won it and
                   takes far less room than a dead button -->
              <div
                v-else-if="event.state === 'finished'"
                class="text-center text-body-2 text-medium-emphasis py-2"
              >
                <template v-if="winner">
                  <v-icon size="18" class="mr-1">mdi-trophy-variant</v-icon>
                  {{ $t('events.winner') }}: <UserName :name="winner" class="font-weight-medium" />
                </template>
                <!-- Finished without a single result: there is no winner to name -->
                <template v-else>
                  <v-icon size="18" class="mr-1">mdi-flag-checkered</v-icon>
                  {{ $t('events.finished') }}
                </template>
              </div>
              <v-btn v-else disabled size="large" class="w-100">
                {{ $t('events.notPlayable') }}
              </v-btn>
              <!-- The gate of an event that asks for an explicit activation (a stage): right
                   under the play button, and only where the component has something to offer. -->
              <EventActivationButton
                :event="event"
                class="mt-2"
                @change="applyActivation"
              />
              <!-- Outside the chain above, and under the play button: the second chance of a
                   one-attempt type. The component decides whether it has anything to offer (see
                   components/EventResetButton.vue). -->
              <EventResetButton :event="event" class="mt-2" @reset="applyReset" />

              <v-divider class="my-4" />

              <div class="d-flex align-center justify-space-between ga-3 py-2">
                <span class="text-caption text-medium-emphasis">{{ $t('events.type') }}</span>
                <ChipEventType :type="event.eventType" />
              </div>

              <div class="d-flex align-center justify-space-between ga-3 py-2">
                <span class="text-caption text-medium-emphasis">{{ $t('events.state') }}</span>
                <v-chip :color="stateColor(event.state)" size="small" variant="tonal" label>
                  {{ $t(`eventStates.${event.state}`) }}
                </v-chip>
              </div>

              <div class="d-flex align-center justify-space-between ga-3 py-2">
                <span class="text-caption text-medium-emphasis">{{ $t('events.dates') }}</span>
                <span class="text-body-2 text-medium-emphasis">
                  <DateRange :from="event.dateStart" :to="event.dateEnd" />
                </span>
              </div>

              <!-- The window is UTC: the closing time is spelled out, otherwise the day looks
                   wrong to anybody east of Greenwich (23:59 UTC is already the next day there) -->
              <div class="d-flex align-center justify-space-between ga-3 py-2">
                <span class="text-caption text-medium-emphasis">{{ $t('events.deadline') }}</span>
                <span class="text-body-2 text-medium-emphasis">{{ formatUtcTime(event.dateEnd) }}</span>
              </div>

              <div v-if="event.usesLaps" class="d-flex align-center justify-space-between ga-3 py-2">
                <span class="text-caption text-medium-emphasis">{{ $t('events.laps') }}</span>
                <span class="text-body-2">{{ event.defaultLaps }}</span>
              </div>

              <div class="d-flex align-center justify-space-between ga-3 py-2">
                <span class="text-caption text-medium-emphasis">{{ $t('events.yourResult') }}</span>
                <div v-if="event.myEntry" class="d-flex align-center ga-1">
                  <v-chip color="primary" size="small" variant="flat" label>
                    {{ $t('events.position') }} {{ event.myEntry.position }}
                  </v-chip>
                  <ChipPoints v-if="event.myEntry.points !== null" :points="event.myEntry.points" />
                  <v-chip v-else-if="event.myEntry.resultTime" size="small" variant="tonal" label>
                    {{ event.myEntry.resultTime }}
                  </v-chip>
                </div>
                <span v-else class="text-body-2 text-medium-emphasis">–</span>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Standings -->
        <v-card elevation="3" rounded="lg" class="mt-6 mb-6 overflow-hidden">
          <div class="d-flex align-center ga-3 pa-4">
            <v-avatar color="amber-darken-2" variant="tonal" size="48">
              <v-icon size="28">mdi-trophy-variant</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
              <h2 class="text-h6 font-weight-bold">{{ $t('events.standings') }}</h2>
            </div>

            <!-- The same standings read the other way round: one column per track, where the points
                 of everybody were earned — or the gap to the best time, for a time event. -->
            <v-btn-toggle
              v-if="event.pointsBased || isTimeBased"
              v-model="standingsView"
              density="compact"
              variant="tonal"
              divided
              mandatory
            >
              <v-btn
                :value="VIEW.TRACKS"
                icon="mdi-view-column-outline"
                size="small"
                :title="$t(event.pointsBased ? 'events.viewByTrack' : 'events.viewByTrackTimes')"
              />
              <v-btn
                :value="VIEW.LIST"
                icon="mdi-table-large"
                size="small"
                :title="$t('events.viewByList')"
              />
            </v-btn-toggle>
          </div>

          <v-divider />

          <!-- Columns per track: the points every player took, or the gap to the best time. -->
          <TrackPointsTable
            v-if="showTrackColumns && event.pointsBased"
            :rows="standingsRows"
            :track-keys="trackKeys"
          />

          <TrackTimesTable
            v-else-if="showTrackColumns"
            :rows="timeRows"
            :track-keys="trackKeys"
          />

          <v-table v-else-if="event.standings.length" hover>
            <thead>
              <tr>
                <th class="text-center" style="width: 56px">#</th>
                <th>{{ $t('events.player') }}</th>
                <!-- A points-based event is won on points: the sum of the times is only the last
                     tie-break, so it stays out of the table -->
                <th v-if="!event.pointsBased" class="text-center">{{ $t('events.result') }}</th>
                <th v-if="event.pointsBased" class="text-center">{{ $t('events.points') }}</th>
                <!-- For a time event the total is comparable only knowing how many tracks it
                     was made on. -->
                <th v-if="!event.pointsBased" class="text-center">{{ $t('events.played') }}</th>
                <th class="text-center">{{ $t('events.wins') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in event.standings"
                :key="row.userId"
                :class="{ 'me-row': row.isMe }"
              >
                <td class="text-center">
                  <v-icon v-if="row.position <= 3" :class="podiumClass(row.position)" size="20">mdi-medal</v-icon>
                  <span v-else class="text-medium-emphasis">{{ row.position }}</span>
                </td>
                <td class="font-weight-medium"><UserName :name="row.playerName" /></td>
                <td v-if="!event.pointsBased" class="text-center text-mono">{{ row.resultTime ?? '–' }}</td>
                <td v-if="event.pointsBased" class="text-center font-weight-bold">
                  {{ row.points ?? '–' }}
                </td>
                <td v-if="!event.pointsBased" class="text-center">{{ row.played }}</td>
                <td class="text-center">
                  <span v-if="row.wins">{{ row.wins }}</span>
                  <span v-else class="text-medium-emphasis">–</span>
                </td>
              </tr>
            </tbody>
          </v-table>

          <div v-else class="d-flex align-center ga-2 pa-4 text-medium-emphasis">
            <v-icon size="20">mdi-trophy-outline</v-icon>
            <span class="text-caption">{{ $t('events.noStandings') }}</span>
          </div>

          <!-- Legend of the standings, under the table it refers to -->
          <p
            v-if="event.standings.length"
            class="text-caption text-medium-emphasis px-4 pb-4 pt-2 mb-0 mt-2"
          >
            <template v-if="event.pointsBased">
              {{ $t('events.pointsLegend') }}:
              {{ event.pointsPerPosition.map((points, index) => `${index + 1}° ${points}`).join(' · ') }}
            </template>
            <template v-else>{{ $t('events.timeBasedLegend') }}</template>
          </p>
        </v-card>
      </template>
    </v-container>

    <!-- Outside the loaded-state block: the dialog has to stay mounted while it closes. -->
    <ConfirmDialog
      v-model="retryConfirming"
      :title="$t('events.retryTitle')"
      :text="retryText"
      :confirm-text="$t('events.retryConfirm')"
      :cancel-text="$t('common.cancel')"
      confirm-color="warning"
      @confirm="retryStage"
    />
  </main>
</template>

<style scoped>
.me-row td {
  background-color: rgba(var(--v-theme-primary), 0.06);
}
</style>
